import { useCallback, useState } from 'react';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { useGetParamsData } from 'src/containers/MainPageApartments/hooks';
import { useStandardContactForm, useGetRequestAppData } from 'src/hooks/app';
import { useBuildType, useInitEmbeddedJS } from 'src/hooks';
import { ListOfKeysFilterAllData } from 'src/typings/filter';
import { getComplexId } from 'src/store/app/selectors';
import { allUtms } from 'src/constants/config';
import { FormFields } from 'src/typings/forms';
import { useBookNowForm } from 'src/hooks/useBookNowForm';
import { translations } from 'src/constants/translations';
import { getComplexDefaultConfig } from 'src/api';
import { IComplex } from 'src/typings/complexes';
import { getMarketplaceLanguage } from 'src/store/marketplace/selectors';
import { getCustomParamsFromCookie } from 'src/hooks/useCustomGetParams';

function getGAclientId() {
  let match = document.cookie.match('(?:^|;)\\s*_ga=([^;]*)');
  const raw = (match) ? decodeURIComponent(match[1]) : null;
  if (raw) {
    match = raw.match(/(\d+\.\d+)$/);
  }
  const gacid = (match) ? match[1] : null;
  if (gacid) {
    return gacid;
  }
  return null;
}

export const useSendForm = () => {
  const errorInitialState = { state: false, msg: '' };
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false);
  const [isError, setIsError] = useState<{ state: boolean, msg: string }>(errorInitialState);
  const complexId = useSelector(getComplexId);
  const { isCrossTable } = useBuildType();
  const { sendBookNowForm } = useBookNowForm();
  const { standardForm } = useStandardContactForm();
  const { handleOnFormSubmit } = useInitEmbeddedJS();
  const { appRequestData } = useGetRequestAppData();
  const {
    isSingleId,
    chooseData: placementData,
  } = useGetParamsData();
  const { isMarketplace } = useBuildType();
  const marketplaceLang = useSelector(getMarketplaceLanguage);

  const handleSendForm = useCallback(async (data: any, _complex?: Partial<IComplex> | null, customPlacementData?: any) => {
    let complexDefaultConfig;
    if (complexId) {
      complexDefaultConfig = await getComplexDefaultConfig(complexId, '');
    }
    if (_complex?.id) {
      complexDefaultConfig = await getComplexDefaultConfig(_complex?.id, '');
    }
    if (complexDefaultConfig && !isSending) {
      setIsSending(true);
      setIsError(errorInitialState);

      const _standardForm = standardForm || complexDefaultConfig.data.client_module_standard_contact_form;
      const formatedData = (
        _standardForm
        && _standardForm.bio
        && _standardForm.phoneNumber
        && _standardForm.additionalInfo
      ) ? {
          [FormFields.bio]: data[FormFields.bio],
          [FormFields.phone]: data[FormFields.phone],
          [FormFields.additionalInfo]: `${data.email}|${data.url}|${data.additionalInfo}`,
        } : data;

      formatedData[FormFields.email] = data.email;
      formatedData[FormFields.url] = data.url;
      formatedData[FormFields.comment] = data.additionalInfo;
      const gaClientId = getGAclientId();

      if (gaClientId) {
        formatedData[FormFields.googleClientId] = gaClientId;
      }

      try {
        const res = await fetch('https://api.ipify.org/?format=json');
        const ipres = await res.json();
        formatedData[FormFields.clientIp] = ipres.ip;
      } catch (error) {
        console.log('ADBLOCK ENABLE, I DONT KNOWN IP');
      }

      const utmString = allUtms.reduce((acc: string, item: string) => {
        if (Cookies.get(item)) {
          formatedData[item] = Cookies.get(item);
          return `${acc} ${item},`;
        }
        return acc;
      }, '');
      const hiddenInputs: any = _standardForm?.static_hidden_inputs;
      const title = isCrossTable ? appRequestData?.crosstable_title : appRequestData?.module_title;

      formatedData[FormFields.source] = 'FLAT.SHOW';
      formatedData[FormFields.utm] = utmString;
      formatedData[FormFields.complexId] = complexId || _complex?.id;
      if (!isMarketplace) {
        if (placementData) {
          if (isSingleId) {
            formatedData[FormFields.placementAllocation] = `${title} - ${
              placementData[ListOfKeysFilterAllData.placementType]} - ${translations.building} ${
              placementData[ListOfKeysFilterAllData.placementHouse]} - ${translations.section} ${placementData[ListOfKeysFilterAllData.placementSection]} - ${translations.floor} ${
              placementData[ListOfKeysFilterAllData.placementFloor]} - ${translations.unitNumber} ${
              placementData[ListOfKeysFilterAllData.placementNumeration]} - ${translations.totalArea} ${
              placementData[ListOfKeysFilterAllData.placementTotalArea]}, ${translations.layoutType} ${placementData[ListOfKeysFilterAllData.placementPlanningType]}`;
          } else {
            formatedData[FormFields.placementAllocation] = `${title} - ${
              placementData[ListOfKeysFilterAllData.placementType]} - ${translations.building} ${
              placementData[ListOfKeysFilterAllData.placementHouse]} - ${translations.section} ${
              placementData[ListOfKeysFilterAllData.placementSection]} - ${translations.totalArea} ${
              placementData[ListOfKeysFilterAllData.placementTotalArea]}, ${translations.layoutType} ${placementData[ListOfKeysFilterAllData.placementPlanningType]}`;
          }
        }
      } else if (customPlacementData && marketplaceLang) {
        formatedData[FormFields.placementAllocation] = `${complexDefaultConfig.data.default_title} - ${
          customPlacementData.placementType.title[marketplaceLang]} - ${translations.building} ${
          customPlacementData.house.numeration[marketplaceLang]} - ${translations.section} ${customPlacementData.section.numeration[marketplaceLang]} - ${translations.floor} ${
          customPlacementData.floors[0].numeration[marketplaceLang]} - ${translations.unitNumber} ${
          customPlacementData.numeration[marketplaceLang]} - ${translations.totalArea} ${
          customPlacementData.totalArea}, ${translations.layoutType} ${customPlacementData.planningType[marketplaceLang]}`;
      }
      if (hiddenInputs) {
        Object.keys(hiddenInputs).forEach((key) => {
          formatedData[key] = hiddenInputs[key];
        });
      }
      // какой-то костыль чтоб на лету добавлять поля
      if (window.getDynamicFields) {
        const dynamicFields = window.getDynamicFields();
        Object.keys(dynamicFields).forEach((key) => {
          formatedData[key] = dynamicFields[key];
        });
      }

      const customParams = getCustomParamsFromCookie();
      if (customParams && Object.keys(customParams).length) {
        Object.keys(customParams).forEach((key) => {
          formatedData[key] = customParams[key];
        });
      }

      try {
        const res = await sendBookNowForm(formatedData);
        setIsSending(false);
        if (res.data.error) {
          setIsError(res.data.error);
          setIsError({
            state: res.data.error,
            msg: res.data.message,
          });
        } else {
          setIsDone(true);
          handleOnFormSubmit(complexDefaultConfig.data.client_module_outer_embedded_js);
          const eventName = 'request_plan';
          if (window.dataLayer) {
            console.log('GTM request_plan');
            window.dataLayer.push({ event: eventName });
          }
          if (window.parent && window.parent !== window) {
            const targetOrigin = window.realClientPageUrl && window.realClientPageUrl !== 'CLIENT_PAGE_URL'
              ? window.realClientPageUrl
              : '*';
            window.parent.postMessage({
              source: 'flatshow_iframe',
              event: eventName,
            }, targetOrigin);
          }
          if (_standardForm && _standardForm.redirect_url) {
            window.location.replace(_standardForm?.redirect_url);
          }
        }
      } catch (error) {
        setIsSending(false);
        setIsError({
          state: true,
          msg: 'Something went wrong',
        });
      }
    }
  }, [isSending, placementData, marketplaceLang, errorInitialState, standardForm]);

  return {
    isSending,
    isDone,
    isError,
    handleSendForm,
  };
};
